import { cn } from '@src/lib/utils';

import { Label } from '@radix-ui/react-label';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { cva, cx, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

/* =======================
   Track variants
======================= */

const switchVariants = cva(
    'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:!cursor-not-allowed disabled:opacity-50 data-[disabled]:!cursor-not-allowed data-[state=checked]:bg-[var(--color-brand-green)] data-[state=unchecked]:bg-[var(--color-switch-track-unchecked)]',
    {
        variants: {
            orientation: {
                horizontal: '',
                vertical: 'flex-col',
            },
            size: {
                sm: '',
                md: '',
                lg: '',
            },
        },
        defaultVariants: {
            orientation: 'horizontal',
            size: 'md',
        },
    },
);

/* =======================
   Size maps
======================= */

const sizeConfig = {
    sm: {
        horizontal: {
            track: 'h-5 w-9',
            thumb: 'h-4 w-4',
            translate: 'data-[state=checked]:translate-x-4',
        },
        vertical: {
            track: 'h-9 w-5',
            thumb: 'h-4 w-4',
            translate: 'data-[state=checked]:translate-y-4',
        },
    },
    md: {
        horizontal: {
            track: 'h-6 w-11',
            thumb: 'h-5 w-5',
            translate: 'data-[state=checked]:translate-x-5',
        },
        vertical: {
            track: 'h-11 w-6',
            thumb: 'h-5 w-5',
            translate: 'data-[state=checked]:translate-y-5',
        },
    },
    lg: {
        horizontal: {
            track: 'h-7 w-14',
            thumb: 'h-6 w-6',
            translate: 'data-[state=checked]:translate-x-7',
        },
        vertical: {
            track: 'h-14 w-7',
            thumb: 'h-6 w-6',
            translate: 'data-[state=checked]:translate-y-7',
        },
    },
};

/* =======================
   Props
======================= */

export interface SwitchProps
    extends
        Omit<
            React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root>,
            'checked' | 'defaultChecked' | 'value' | 'onChange'
        >,
        VariantProps<typeof switchVariants> {
    checked?: boolean;
    defaultChecked?: boolean;
    value?: boolean;
    onChange?: (checked: boolean) => void;

    title?: string;
    subTitle?: string;
    readOnly?: boolean;
}

/* =======================
   Component
======================= */

const SwitchFlow = React.forwardRef<
    React.ElementRef<typeof SwitchPrimitives.Root>,
    SwitchProps
>(
    (
        {
            className,
            onChange,
            value,
            orientation = 'horizontal',
            size = 'md',
            readOnly,
            ...props
        },
        ref,
    ) => {
        const { t } = useTranslation();

        const config = sizeConfig[size!][orientation!];

        if (readOnly) {
            return (
                <div className={cn('flex flex-col gap-1')}>
                    <span className='text-xs font-normal text-(--color-grey-text)'>
                        {props.title}
                    </span>
                    <div className='font-inter flex flex-col gap-1 text-sm font-semibold text-(--color-simple)'>
                        {value ? t('translation:yes') : t('translation:no')}
                    </div>
                </div>
            );
        }

        return (
            <div
                className={cx(
                    'flex items-center gap-4',
                    props.title &&
                        'overflow-hidden rounded-xl border px-2.5 py-3',
                )}
            >
                <SwitchPrimitives.Root
                    ref={ref}
                    className={cn(
                        switchVariants({ orientation, size }),
                        config.track,
                        className,
                    )}
                    {...props}
                    checked={value}
                    id={props.id ?? 'switch'}
                    onCheckedChange={onChange}
                >
                    <SwitchPrimitives.Thumb
                        className={cn(
                            'pointer-events-none block rounded-full bg-[var(--color-switch-thumb-unchecked)] transition-transform data-[state=checked]:bg-[var(--color-switch-thumb-checked)]',
                            config.thumb,
                            config.translate,
                            'data-[state=unchecked]:translate-x-0 data-[state=unchecked]:translate-y-0',
                        )}
                    />
                </SwitchPrimitives.Root>

                {props.title && (
                    <Label
                        className='flex min-w-0 flex-col gap-1'
                        htmlFor={props.id ?? 'switch'}
                    >
                        <span className='truncate text-sm font-semibold text-(--color-select-text)'>
                            {props.title}
                        </span>
                        {props.subTitle && (
                            <div className='block truncate text-sm font-normal'>
                                {props.subTitle}
                            </div>
                        )}
                    </Label>
                )}
            </div>
        );
    },
);

SwitchFlow.displayName = SwitchPrimitives.Root.displayName;

export { SwitchFlow };
